import React, { useState, useEffect } from "react";
import { FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaStar } from "react-icons/fa";
import {
  Form,
  Label,
  Input,
  Card,
  CardBody,
  Button,
  FormGroup
} from "reactstrap";
import { LoadScript, Autocomplete } from "@react-google-maps/api";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Flatpickr from "react-flatpickr";

import "flatpickr/dist/themes/material_blue.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../../assets/FrontEnd/css/style.css";

const FrontPageSearch = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [pickup, setPickup] = useState({ address: "", lat: null, lng: null });
  const [dropoff, setDropoff] = useState({ address: "", lat: null, lng: null });
  const [date, setDate] = useState(new Date());
  const [passenger, setPassenger] = useState(1);
  const [isMapsLoaded, setIsMapsLoaded] = useState(false);

  const [rides, setRides] = useState([]);
  const [loading, setLoading] = useState(false);

  const increasePassenger = () => setPassenger((prev) => prev + 1);
  const decreasePassenger = () =>
    setPassenger((prev) => (prev > 1 ? prev - 1 : 1));

  useEffect(() => {
    const query = new URLSearchParams(location.search);

    const pickup_address = query.get("pickup_address") || "";
    const pickup_lat = query.get("pickup_lat")
      ? parseFloat(query.get("pickup_lat"))
      : null;
    const pickup_lng = query.get("pickup_lng")
      ? parseFloat(query.get("pickup_lng"))
      : null;

    const dropoff_address = query.get("dropoff_address") || "";
    const dropoff_lat = query.get("dropoff_lat")
      ? parseFloat(query.get("dropoff_lat"))
      : null;
    const dropoff_lng = query.get("dropoff_lng")
      ? parseFloat(query.get("dropoff_lng"))
      : null;

    const queryDate = query.get("date");
    const queryPassengers = query.get("passengers")
      ? parseInt(query.get("passengers"))
      : 1;

    if (pickup_address)
      setPickup({ address: pickup_address, lat: pickup_lat, lng: pickup_lng });
    if (dropoff_address)
      setDropoff({
        address: dropoff_address,
        lat: dropoff_lat,
        lng: dropoff_lng,
      });
    if (queryDate) setDate(new Date(queryDate));
    setPassenger(queryPassengers);

    fetchRides({
      pickup_address,
      pickup_lat,
      pickup_lng,
      dropoff_address,
      dropoff_lat,
      dropoff_lng,
      date: queryDate,
      passengers: queryPassengers,
    });
  }, [location.search]);

  const fetchRides = async (params) => {
    try {
      setLoading(true);
      const queryString = new URLSearchParams(params).toString();
      let url =
        process.env.REACT_APP_WEB_APP_URL + `/api/rides?${queryString}`;
      const res = await fetch(url);
      const data = await res.json();
      setRides(data.data);
    } catch (error) {
      console.error("Error fetching rides:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const params = new URLSearchParams({
      pickup_address: pickup.address,
      pickup_lat: pickup.lat,
      pickup_lng: pickup.lng,
      dropoff_address: dropoff.address,
      dropoff_lat: dropoff.lat,
      dropoff_lng: dropoff.lng,
      date: date.toISOString().split("T")[0],
      passengers: passenger,
    }).toString();

    navigate(`/search?${params}`);
  };

  document.title = "Search | Sawari Online";

  return (
    <div className="content checkout-sec py-4">
      <div className="container">
        <div className="row g-4">
          {/* Left Column */}
          <div className="col-lg-8">
            {/* Search Form */}
            <div className="card shadow-lg border-0 rounded-4 mb-4">
              <div className="card-body p-4">
                <h4 className="fw-bold mb-3 text-primary">🚖 Find Your Ride</h4>
                <LoadScript
                  googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAP_API_KEY}
                  libraries={["places"]}
                  onLoad={() => setIsMapsLoaded(true)}
                >
                  <form className="row g-3" onSubmit={handleSubmit}>
                    {/* Pickup */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        <FaMapMarkerAlt className="me-2 text-primary" />
                        Leaving From
                      </label>
                      {isMapsLoaded ? (
                        <Autocomplete
                          options={{
                            componentRestrictions: { country: "pk" },
                          }}
                          onLoad={(auto) => (window.pickupAutoComplete = auto)}
                          onPlaceChanged={() => {
                            const place = window.pickupAutoComplete.getPlace();
                            setPickup({
                              address: place.formatted_address,
                              lat: place.geometry.location.lat(),
                              lng: place.geometry.location.lng(),
                            });
                          }}
                        >
                          <input
                            type="text"
                            className="form-control rounded-pill shadow-sm"
                            placeholder="Enter pickup location"
                            value={pickup.address}
                            onChange={(e) =>
                              setPickup((prev) => ({
                                ...prev,
                                address: e.target.value,
                              }))
                            }
                            required
                          />
                        </Autocomplete>
                      ) : (
                        <input
                          type="text"
                          className="form-control rounded-pill shadow-sm"
                          placeholder="Loading..."
                          disabled
                        />
                      )}
                    </div>

                    {/* Dropoff */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        <FaMapMarkerAlt className="me-2 text-danger" />
                        Going To
                      </label>
                      {isMapsLoaded ? (
                        <Autocomplete
                          options={{
                            componentRestrictions: { country: "pk" },
                          }}
                          onLoad={(auto) => (window.dropoffAutoComplete = auto)}
                          onPlaceChanged={() => {
                            const place =
                              window.dropoffAutoComplete.getPlace();
                            setDropoff({
                              address: place.formatted_address,
                              lat: place.geometry.location.lat(),
                              lng: place.geometry.location.lng(),
                            });
                          }}
                        >
                          <input
                            type="text"
                            className="form-control rounded-pill shadow-sm"
                            placeholder="Enter dropoff location"
                            value={dropoff.address}
                            onChange={(e) =>
                              setDropoff((prev) => ({
                                ...prev,
                                address: e.target.value,
                              }))
                            }
                            required
                          />
                        </Autocomplete>
                      ) : (
                        <input
                          type="text"
                          className="form-control rounded-pill shadow-sm"
                          placeholder="Loading..."
                          disabled
                        />
                      )}
                    </div>

                    {/* Date */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        <FaCalendarAlt className="me-2 text-success" />
                        Select Date
                      </label>
                      <Flatpickr
                        className="form-control rounded-pill shadow-sm"
                        value={date}
                        options={{
                          altInput: true,
                          altFormat: "F j, Y",
                          dateFormat: "Y-m-d",
                          minDate: "today",
                        }}
                        onChange={(dates) => setDate(dates[0])}
                      />
                    </div>

                    {/* Passengers */}
                    <div className="col-md-6">
                      <label className="form-label fw-semibold">
                        <FaUsers className="me-2 text-info" />
                        Passengers
                      </label>
                      <div className="input-group">
                        <button
                          type="button"
                          className="btn btn-outline-secondary rounded-start-pill"
                          onClick={decreasePassenger}
                        >
                          -
                        </button>
                        <input
                          type="text"
                          className="form-control text-center shadow-sm"
                          value={passenger}
                          readOnly
                        />
                        <button
                          type="button"
                          className="btn btn-outline-secondary rounded-end-pill"
                          onClick={increasePassenger}
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Search Button */}
                    <div className="col-md-12 d-grid mt-3">
                      <Button
                        type="submit"
                        color="primary"
                        className="rounded-pill py-2 fw-bold"
                      >
                        🔍 Search
                      </Button>
                    </div>
                  </form>
                </LoadScript>
              </div>
            </div>

            {/* Ride Results */}
            <div className="row g-3">
              {rides.map((item) => (
                <div className="col-12" key={item.booking.id}>
                  <Link
                    to={`/booking/${item.booking.id}`}
                    className="card shadow-sm border-0 rounded-4 p-3 h-100 text-decoration-none text-dark hover-shadow"
                  >
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <img
                          src={item.driver.image}
                          alt="avatar"
                          className="rounded-circle"
                          style={{
                            width: "45px",
                            height: "45px",
                            objectFit: "cover",
                          }}
                        />
                        <div>
                          <h6 className="mb-0 fw-bold">{item.driver.name}</h6>
                          <span className="text-warning small">
                            <FaStar /> 4.8
                          </span>
                        </div>
                      </div>
                      <span className="badge bg-primary fs-6">
                        PKR {item.vehicle.seat_rate}
                      </span>
                    </div>

                    <div className="d-flex align-items-center justify-content-between mt-2">
                      {/* Pickup */}
                      <div>
                        <p className="text-muted small mb-1">Pickup</p>
                        <p className="fw-semibold mb-0">
                          {item.booking.pickup_addr}
                        </p>
                        <small className="text-secondary">
                          {item.booking.booking_time}
                        </small>
                      </div>

                      {/* Line */}
                      <div className="flex-grow-1 mx-2 border-top"></div>

                      {/* Dropoff */}
                      <div className="text-end">
                        <p className="text-muted small mb-1">Dropoff</p>
                        <p className="fw-semibold mb-0">
                          {item.booking.dest_addr}
                        </p>
                        <small className="text-secondary">
                          {item.booking.booking_time}
                        </small>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column - Sort */}
          <div className="col-lg-4">
            <div className="sticky-top" style={{ top: "20px" }}>
              <Card className="shadow-sm border-0 rounded-4">
                <div className="card-header bg-white border-0">
                  <h5 className="fw-bold mb-0">Sort By</h5>
                </div>
                <CardBody>
                  <Form>
                    <FormGroup check className="mb-2">
                      <Input type="radio" name="sortBy" id="sort1" defaultChecked />
                      <Label for="sort1" check>
                        Lowest Price
                      </Label>
                    </FormGroup>
                    <FormGroup check className="mb-2">
                      <Input type="radio" name="sortBy" id="sort2" />
                      <Label for="sort2" check>
                        Highest Price
                      </Label>
                    </FormGroup>
                    <FormGroup check className="mb-2">
                      <Input type="radio" name="sortBy" id="sort3" />
                      <Label for="sort3" check>
                        Earliest Departure
                      </Label>
                    </FormGroup>
                    <FormGroup check className="mb-2">
                      <Input type="radio" name="sortBy" id="sort4" />
                      <Label for="sort4" check>
                        Latest Departure
                      </Label>
                    </FormGroup>
                  </Form>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FrontPageSearch;
